/**
 * EmailEnrollmentModal Component
 *
 * Modal dialog for adding email authentication method.
 * Allows users to enroll a personal email address for MFA.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Field,
  Input,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../../common/AsyncButton';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

interface EmailEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onEnroll: (emailAddress: string) => Promise<void>;
}

export const EmailEnrollmentModal: React.FC<EmailEnrollmentModalProps> = ({
  isOpen,
  onClose,
  onEnroll,
}) => {
  const styles = useStyles();
  const [emailAddress, setEmailAddress] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmailAddress('');
      setValidationError('');
      setIsEnrolling(false);
    }
  }, [isOpen]);

  // Email validation regex (simple RFC 5322 compliance check)
  const validateEmail = (email: string): boolean => {
    if (!email || email.trim() === '') {
      setValidationError('Email address is required');
      return false;
    }

    // Simple email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setValidationError('Please enter a valid email address');
      return false;
    }

    setValidationError('');
    return true;
  };

  const handleSubmit = async () => {
    // Validate email
    if (!validateEmail(emailAddress)) {
      return;
    }

    try {
      setIsEnrolling(true);
      await onEnroll(emailAddress);

      // Clear form after successful enrollment
      setEmailAddress('');
      setValidationError('');
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleCancel = () => {
    setEmailAddress('');
    setValidationError('');
    onClose();
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailAddress(event.target.value);
    // Clear validation error when user starts typing
    if (validationError) {
      setValidationError('');
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Add Email Authentication Method</DialogTitle>
          <DialogContent className={styles.content}>
            <p>
              Add a personal email address to receive verification codes for
              multi-factor authentication.
            </p>
            <Field
              label="Email Address"
              required
              validationMessage={validationError}
              validationState={validationError ? 'error' : 'none'}
            >
              <Input
                type="email"
                value={emailAddress}
                onChange={handleEmailChange}
                placeholder="personal@example.com"
                disabled={isEnrolling}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={handleCancel}
              disabled={isEnrolling}
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSubmit}
              disabled={isEnrolling}
              loading={isEnrolling}
              loadingText="Adding..."
            >
              Add
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
